import * as React from 'react';
import * as _ from 'underscore';
import { PartialSummer } from './FastPartialSum';
import { LazyListRow } from './LazyListRow';

export interface LazyListWidgetProps {
  readonly rowCount: number;
  readonly rowHeight: number; // if using dynamic row heights, this should be an _estimated_ row height
  readonly rowToComponent: (index: number) => React.ReactElement;
  readonly buffer?: number;
}

export interface LazyListWidgetState {
  readonly firstIndex: number;
  readonly renderRowCount: number;
}

export class LazyListWidget extends React.PureComponent<LazyListWidgetProps, LazyListWidgetState> {
  public state: LazyListWidgetState = {
    firstIndex: 0,
    renderRowCount: 1 + 2 * this.buffer,
  };
  private summer: PartialSummer = new PartialSummer({ maximumLength: this.props.rowCount, defaultValue: this.props.rowHeight });
  private containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  public render(): JSX.Element {
    return <div
      style={{ overflowY: 'auto'}}
      onScroll={this.handleScroll}
      ref={this.containerRef} >
      <div style={{ height: this.summer.getTotalSum(), position: 'relative', overflowAnchor: 'auto'}}>
        {this.estimatedRowsToRender.map(this.rowToMeasuredComponent)}
      </div>
    </div >;
  }

  // public shouldComponentUpdate(nextProps: LazyListWidgetProps<T>, nextState: LazyListWidgetState): boolean {
  //   return nextProps !== this.props ||
  //     nextState.firstIndex !== this.state.firstIndex ||
  //     nextState.renderRowCount !== this.state.renderRowCount;
  // }

  public componentDidMount(): void {
    this.containerRef.current!.scrollTop = 0;
    this.setState({ renderRowCount: Math.ceil(window.innerHeight / this.props.rowHeight) + 2 * this.buffer });
  }

  public componentDidUpdate(prevProps: LazyListWidgetProps): void {
    if (this.props.rowCount !== prevProps.rowCount) {
      this.containerRef.current!.scrollTop = 0;
      this.summer = new PartialSummer({ maximumLength: this.props.rowCount, defaultValue: this.props.rowHeight });
      this.setState({ firstIndex: 0 });
    }
  }

  private handleScroll = (e: React.UIEvent<HTMLDivElement> & {target: HTMLDivElement}) => this.updateContentsOnScroll(e.target.scrollTop);

  private updateContentsOnScroll = _.throttle((scrollTop: number) => {
    this.setState({ firstIndex: Math.max(this.summer.getIndexForSum(scrollTop) - this.buffer, 0) });
  }, 50);

  private get buffer(): number {
    return this.props.buffer !== undefined ? this.props.buffer : 5;
  }

  private get rowToMeasuredComponent(): (rowIndex: number) => React.ReactElement {
    return (rowIndex: number): React.ReactElement => <LazyListRow
      key={rowIndex}
      offset={this.summer.getSumToIndex(this.state.firstIndex)}
      announceHeight={this.summer.setIndexToValue}
      id={rowIndex}
      children={this.props.rowToComponent(rowIndex)}/>;
  }

  private get estimatedRowsToRender(): ReadonlyArray<number> {
    const rowsToRender: number[] = [];
    for(let index = this.state.firstIndex; index < Math.min(this.state.firstIndex + this.state.renderRowCount, this.props.rowCount); index += 1) {
      rowsToRender.push(index);
    }
    return rowsToRender;
  }
}
